// client/src/pages/POA/usePOAForm.ts
// Form state management with sessionStorage persistence.

import { useCallback, useEffect, useState } from 'react';
import type { POAFormState, POARepresentative, POATaxMatter, POAPrefillData, POAPrefillIndividual } from '../../types/poa';
import {
  DEFAULT_CIVIL_PENALTY_COVERAGE,
  CIVIL_PENALTY_DESCRIPTION,
  CIVIL_PENALTY_FORM_NUMBER,
} from './poaConstants';

const SESSION_KEY = 'poa-new-draft';

function makeRep(order: number): POARepresentative {
  return {
    id: crypto.randomUUID(),
    repOrder: order,
    name: '', address: '', cafNumber: '', ptin: '',
    phone: '', fax: '',
    receivesNotices: order === 1, // first rep gets notices by default
    isNewAddress: false, isNewPhone: false, isNewFax: false,
    designationCode: 'b', // CPA is the most common designation for this firm
    licensingJurisdiction: 'Alabama',
    licenseNumber: '',
  };
}

function makeMatter(): POATaxMatter {
  return {
    id: crypto.randomUUID(),
    matterDescription: '',
    taxFormNumber: '',
    periodType: 'year',
    periodStart: new Date().getFullYear(),
    periodEnd: '',
    periodRaw: '',
  };
}

/** True for the row that carries Civil Penalty coverage. */
export function isCivilPenaltyMatter(m: POATaxMatter): boolean {
  return m.matterDescription === CIVIL_PENALTY_DESCRIPTION;
}

/**
 * A Civil Penalty row covering the same period as the primary matter. Starts out
 * mirroring matter #1 so changing the tax years in one place covers both; the mirror
 * detaches the moment someone edits this row's period directly.
 */
function makeCivilPenaltyMatter(primary?: POATaxMatter): POATaxMatter {
  return {
    id: crypto.randomUUID(),
    matterDescription: CIVIL_PENALTY_DESCRIPTION,
    taxFormNumber:     CIVIL_PENALTY_FORM_NUMBER,
    periodType:  primary?.periodType  ?? 'year',
    periodStart: primary?.periodStart ?? new Date().getFullYear(),
    periodEnd:   primary?.periodEnd   ?? '',
    periodRaw:   primary?.periodRaw   ?? '',
    autoMirror:  true,
  };
}

/** Period fields — the ones a mirroring Civil Penalty row tracks. */
const PERIOD_FIELDS: ReadonlyArray<keyof POATaxMatter> =
  ['periodType', 'periodStart', 'periodEnd', 'periodRaw'];

const INITIAL_STATE: POAFormState = {
  peClientCode: '',
  taxpayerName: '',
  taxpayerAddress: '',
  taxpayerEin: '',
  taxpayerSsn: '',
  taxpayerTinType: '',
  taxpayerPhone: '',
  taxpayerPlanNumber: '',
  taxpayerEmail: '',
  representatives: [makeRep(1)],
  taxMatters: DEFAULT_CIVIL_PENALTY_COVERAGE
    ? [makeMatter(), makeCivilPenaltyMatter()]
    : [makeMatter()],
  isSpecificUseNotOnCaf: false,
  authIntermediateServiceProvider: false,
  authDisclosureThirdParties: false,
  authSubstituteAddRep: false,
  authSignReturn: false,
  authOtherActs: '',
  unauthOtherDeletions: '',
  retainPriorPoa: false,
};

function loadDraft(): POAFormState {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (raw) return { ...INITIAL_STATE, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return INITIAL_STATE;
}

export function usePOAForm() {
  const [form, setForm] = useState<POAFormState>(loadDraft);

  // Persist to sessionStorage on every change
  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(form)); } catch { /* ignore */ }
  }, [form]);

  // ── Taxpayer field helpers ──────────────────────────────────────────────
  const setTaxpayerField = useCallback(
    (field: keyof POAFormState, value: string | boolean) =>
      setForm((prev) => ({ ...prev, [field]: value })),
    [],
  );

  // ── Representative helpers ──────────────────────────────────────────────
  const addRep = useCallback(() => {
    setForm((prev) => {
      if (prev.representatives.length >= 4) return prev;
      return {
        ...prev,
        representatives: [
          ...prev.representatives,
          makeRep(prev.representatives.length + 1),
        ],
      };
    });
  }, []);

  const removeRep = useCallback((id: string) => {
    setForm((prev) => {
      const filtered = prev.representatives.filter((r) => r.id !== id);
      return {
        ...prev,
        representatives: filtered.map((r, i) => ({ ...r, repOrder: i + 1 })),
      };
    });
  }, []);

  const updateRep = useCallback(
    (id: string, field: keyof POARepresentative, value: string | boolean | number) =>
      setForm((prev) => ({
        ...prev,
        representatives: prev.representatives.map((r) =>
          r.id === id ? { ...r, [field]: value } : r,
        ),
      })),
    [],
  );

  // ── Tax Matter helpers ──────────────────────────────────────────────────
  const addMatter = useCallback(() => {
    setForm((prev) => ({ ...prev, taxMatters: [...prev.taxMatters, makeMatter()] }));
  }, []);

  const removeMatter = useCallback((id: string) => {
    setForm((prev) => ({
      ...prev,
      taxMatters: prev.taxMatters.filter((m) => m.id !== id),
    }));
  }, []);

  const updateMatter = useCallback(
    (id: string, field: keyof POATaxMatter, value: string | number) =>
      setForm((prev) => {
        const idx = prev.taxMatters.findIndex((m) => m.id === id);
        if (idx === -1) return prev;

        const isPeriodEdit = PERIOD_FIELDS.includes(field);
        let next = prev.taxMatters.map((m) =>
          m.id === id ? { ...m, [field]: value } : m,
        );

        // Editing a mirroring row's own period detaches it — an explicit choice beats
        // the mirror from here on, and we never overwrite that row again.
        if (isPeriodEdit && idx > 0 && next[idx].autoMirror) {
          next[idx] = { ...next[idx], autoMirror: false };
        }

        // Editing matter #1's period carries through to rows still mirroring it.
        if (isPeriodEdit && idx === 0) {
          const primary = next[0];
          next = next.map((m, i) => (i > 0 && m.autoMirror)
            ? {
                ...m,
                periodType:  primary.periodType,
                periodStart: primary.periodStart,
                periodEnd:   primary.periodEnd,
                periodRaw:   primary.periodRaw,
              }
            : m);
        }

        return { ...prev, taxMatters: next };
      }),
    [],
  );

  /** Add Civil Penalty coverage mirroring matter #1. No-op if a row already exists. */
  const addCivilPenaltyMatter = useCallback(() => {
    setForm((prev) => {
      if (prev.taxMatters.some(isCivilPenaltyMatter)) return prev;
      return {
        ...prev,
        taxMatters: [...prev.taxMatters, makeCivilPenaltyMatter(prev.taxMatters[0])],
      };
    });
  }, []);

  // Apply CCH prefill data into taxpayer fields.
  // For individual returns, caller picks 'taxpayer' or 'spouse' before calling.
  //
  // preserveContact is set on the second form of a joint pair. Both spouses share a
  // household address and phone, and by then the user has already had a chance to
  // correct them on the first form — re-applying PE's values would silently discard
  // that correction. It applies to whichever spouse the second form covers, so it is
  // a property of being the second pass, not of being the spouse.
  const applyPrefill = useCallback((
    data: POAPrefillData,
    personKey?: 'taxpayer' | 'spouse',
    preserveContact = false,
  ) => {
    setForm((prev) => {
      if (data.type === 'business') {
        return {
          ...prev,
          taxpayerName:    data.name,
          taxpayerAddress: preserveContact ? (prev.taxpayerAddress || data.address) : data.address,
          taxpayerEin:     data.ein,
          taxpayerSsn:     '',
          taxpayerTinType: 'ein',
          taxpayerPhone:   preserveContact ? (prev.taxpayerPhone || data.phone) : data.phone,
          taxpayerEmail:   data.email || prev.taxpayerEmail,
        };
      }
      // Individual (data.type === 'individual' narrowed by the business guard above)
      const ind      = data as POAPrefillIndividual;
      const isSpouse = personKey === 'spouse' && ind.spouse != null;
      const person   = isSpouse ? ind.spouse! : ind.taxpayer;

      const address = preserveContact
        ? (prev.taxpayerAddress || ind.taxpayer.address)
        : ind.taxpayer.address;
      const phone = preserveContact
        ? (prev.taxpayerPhone || person.phone || ind.taxpayer.phone)
        : (person.phone || ind.taxpayer.phone);

      // A spouse often has no email of their own in PE. Falling back to the taxpayer's
      // keeps the send working; POANewPage surfaces a note when that fallback is used,
      // so nobody discovers it only after the wrong person gets the signature request.
      const email = isSpouse ? (ind.spouse!.email || ind.taxpayer.email) : ind.taxpayer.email;

      return {
        ...prev,
        taxpayerName:    person.name,
        taxpayerAddress: address,
        taxpayerSsn:     person.ssn,
        taxpayerEin:     '',
        taxpayerTinType: 'ssn',
        taxpayerPhone:   phone,
        taxpayerEmail:   email || prev.taxpayerEmail,
      };
    });
  }, []);

  const clearDraft = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setForm(INITIAL_STATE);
  }, []);

  // Reset only the identity fields for the second form of a joint pair (which may cover
  // either spouse) — keeps reps, matters, acts, client code.
  // Address and phone are deliberately kept: joint filers share a household, and what's
  // in the form now already includes any correction made while filling the first form.
  // Generates new UUIDs for reps/matters so the DB doesn't reject duplicates.
  const resetForSpouse = useCallback(() => {
    setForm((prev) => ({
      ...prev,
      taxpayerName: '',
      taxpayerEin: '',
      taxpayerSsn: '',
      taxpayerTinType: '',
      taxpayerPlanNumber: '',
      taxpayerEmail: '',
      representatives: prev.representatives.map((r) => ({
        ...r,
        id: crypto.randomUUID(),
      })),
      taxMatters: prev.taxMatters.map((m) => ({
        ...m,
        id: crypto.randomUUID(),
      })),
    }));
  }, []);

  // Apply a complete form state from AI assist (overwrites everything)
  const applyFormState = useCallback((state: Partial<POAFormState>) => {
    setForm((prev) => {
      const reps = (state.representatives ?? prev.representatives).map((r: any, i: number) => ({
        ...makeRep(i + 1),
        ...r,
        id: r.id || crypto.randomUUID(),
        repOrder: i + 1,
      }));
      const matters = (state.taxMatters ?? prev.taxMatters).map((m: any) => ({
        ...makeMatter(),
        ...m,
        id: m.id || crypto.randomUUID(),
      }));
      return {
        ...prev,
        ...state,
        representatives: reps,
        taxMatters: matters,
      };
    });
  }, []);

  return {
    form,
    setTaxpayerField,
    applyPrefill,
    applyFormState,
    addRep, removeRep, updateRep,
    addMatter, removeMatter, updateMatter, addCivilPenaltyMatter,
    clearDraft,
    resetForSpouse,
  };
}
